import { useId } from 'vue'

/**
 * Stable ids for the text an author already writes around an input, plus the
 * `aria-describedby` composition that points at whichever of it is rendered.
 *
 * The text is on the page but not associated: `BaseInputShell` is a `<label>`
 * that wraps only the field, while the outer label ("How tall are you?") is a
 * sibling ABOVE it and the validation message a sibling BELOW. So a screen
 * reader announces the field with nothing but its inner unit — "years" — and
 * an error that just appeared is never announced at all. Wiring
 * `aria-labelledby` / `aria-describedby` is what connects them.
 *
 * `useId()` is SSR-stable (Vue 3.5), which matters because a built pipe is
 * server-rendered by Nuxt and a hydration-mismatched id would break the
 * association it exists to create.
 */
export function useInputAria() {
  const uid = useId()

  return {
    labelId: `${uid}-label`,
    errorId: `${uid}-error`,
    /**
     * Joins the ids that are actually in the DOM. Returns `undefined` rather
     * than `''` when none are — an empty `aria-describedby` is a dangling
     * reference, which some screen readers report as a broken relationship.
     */
    describedBy: (...ids: (string | false | null | undefined)[]): string | undefined =>
      ids.filter(Boolean).join(' ') || undefined,
  }
}
